import { Control, FormState, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Input } from 'semantic-ui-react';

import { ControllerFormDropdown } from '../../../../components/Form/ControllerFormDropdown';
import { ControllerFormField } from '../../../../components/Form/ControllerFormField';
import { useMandatoryOptions } from '../../../../hooks/useMandatoryOptions';
import { useSbuOptions } from '../../../../hooks/useSbuOptions';
import sbuRegions from '../../../../mocks/sbu.json';
import { getTrainingTypeList } from '../../../../services/training';
import { FIELD_REQUIRED_MESSAGE } from '../../../../settings/constants';
import { ITrainingFilterProps } from '../../../../types/filters';

interface IProps {
  control: Control<ITrainingFilterProps, any>;
  formState: FormState<ITrainingFilterProps>;
  setValue: UseFormSetValue<ITrainingFilterProps>;
}

export function ContentFilter({ control, formState, setValue }: IProps) {
  const { t } = useTranslation();
  const watchRegion = useWatch({ control, name: 'region' });
  const watchSBU = useWatch({ control, name: 'sbuId' });

  const { sbusOptions } = useSbuOptions(watchRegion as string);

  const { mandatoryOptions } = useMandatoryOptions({
    watchSBU: watchSBU as number,
    watchRegion: watchRegion as string,
    setValue,
  });

  const { data: trainingTypeOptions } = useQuery(['trainings'], async () => {
    const data = await getTrainingTypeList();

    return data?.map((item) => ({
      key: item.id,
      text: item.name,
      value: item.id,
    }));
  });

  return (
    <>
      <ControllerFormDropdown
        controllerControl={control}
        controllerName="region"
        label={t('pages.training.sbu')}
        options={sbuRegions}
        required
        error={formState.errors.region && t(FIELD_REQUIRED_MESSAGE)}
        fluid
      />
      <ControllerFormDropdown
        controllerControl={control}
        controllerName="sbuId"
        label={t('pages.training.countryLocation')}
        options={sbusOptions}
        required={!!watchRegion}
        error={formState.errors.sbuId && t(FIELD_REQUIRED_MESSAGE)}
        disabled={!watchRegion}
        fluid
      />
      <ControllerFormDropdown
        controllerControl={control}
        controllerName="mandatoryId"
        label={t('pages.training.year')}
        options={mandatoryOptions}
        required={!!watchSBU}
        error={formState.errors.mandatoryId && t(FIELD_REQUIRED_MESSAGE)}
        disabled={!watchRegion || !watchSBU}
        fluid
      />
      <ControllerFormDropdown
        controllerControl={control}
        controllerName="typeId"
        label={t('pages.training.category')}
        options={trainingTypeOptions}
        fluid
      />
      <ControllerFormField
        controllerControl={control}
        controllerName="description"
        control={Input}
        label={t('pages.training.description')}
        isNotUpperCase
        fluid
      />
      <ControllerFormField
        controllerControl={control}
        controllerName="code"
        control={Input}
        label={t('pages.training.code')}
        fluid
      />
      <ControllerFormField
        controllerControl={control}
        controllerName="icon"
        control={Input}
        label={t('pages.training.icon')}
        isNotUpperCase
        fluid
      />
    </>
  );
}
